import {
  getConvertedPrice,
  RemoteConfig,
  shouldShowMonthly,
} from '@ca/common/types'
import SummaryGroup from '@order/Summary/SummaryGroup'
import { getVariableIcon } from '@ui/elements/Icon/maps'
import { showAdjustedFrameQty, showFrameQty } from '@frame/StockUtils'
import getPackageLabels from '@order/getPackageLabels'
import { OrderState } from '@order/hooks/state/orderState'
import { CartItem } from '@order/hooks/useCart'
import { useDealSettingsById } from '@order/hooks/useDealSettings'
import { OrderItem } from '@order/orderTypes'
import { Discounts } from '@order/utils/discounts/Discounts'
import {
  BundleSummaryItem,
  OrderSummaryItem,
} from '@order/utils/summary/getOrderItemSummary'
import {
  ItemSummary,
  OrderElementExtra,
} from '@order/utils/summary/getOrderPriceSummary'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { SessionDealContext } from 'hooks/useSession'
import { i18n } from 'i18next'
import {
  buildStepTlVariants,
  resolveStepItemKey,
} from '@order/utils/stepTranslations'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
// import useActiveOrderItem from '@order/hooks/useActiveOrderItem'
// import { colors } from '@ui/colors'

const _ = `\u00A0`
const space = `${_}${_}•${_}${_}`

const getTitleEntity = (item?: any): { title: string } | undefined => {
  if (!item || typeof item !== 'object') {
    return undefined
  }
  if (typeof item.title === 'string') {
    return item
  }
  return undefined
}

const variableTls = ({
  variable,
  value,
  config,
  item,
  i18n,
  extra,
}: {
  variable: string
  value: string | number
  extra?: OrderElementExtra
  config: RemoteConfig
  item?: OrderItem
  i18n: i18n
}) => {
  const locale = i18n.language
  const title = `summary:label.${variable}`
  const frameId =
    item?.frame?.attributes.name || item?.frame?.default_code || item?.id

  const frameQty = item?.frame?.store_qty || undefined // WARN: ideally such data should be refetched on render

  const tlVariants = buildStepTlVariants({
    translationPath: extra?.translation?.path,
    type: extra?.translation?.type,
  })
  const basePrefix = `steps:${variable}.items.${value}`
  let desc =
    resolveStepItemKey({
      prefix: basePrefix,
      tlVariants,
      key: 'title',
      i18n,
    }) ?? `${basePrefix}.title`

  if (variable === 'color') {
    const color = Object.values(config.colors)?.find((c) => c.code === value)
    if (color) {
      desc = `steps:color.items.${color.name}.title`
    }
  }
  if (variable === 'deal') {
    const deal =
      getTitleEntity(extra?.deal) ??
      config.deals[locale]?.find((d) => d.code === value)
    if (deal) {
      desc = deal.title
    }
  }
  if (variable === 'frame' && item?.frame) {
    return {
      title: i18n.t(title),
      desc: showFrameQty(item.frame, config.region)
        ? frameId +
          space +
          i18n.t('common:qtyInStore', {
            count: frameQty,
          })
        : frameId,
    }
  }

  if (variable === 'upgrade') {
    const pckg = getPackageLabels({
      value: extra?.variant || config.packages[value].value,
      name: config.packages[value]?.name,
      i18n,
    })

    desc = pckg.title
  }

  return {
    title: i18n.t(title),
    desc: i18n.exists(desc) ? i18n.t(desc) : desc,
  }
}

export const ItemBundle = ({
  item,
  price,
  elements,
  expanded,
  borderBottom,
  deal,
  order,
}: ItemSummary<BundleSummaryItem> & {
  expanded?: boolean
  borderBottom?: boolean
  deal?: SessionDealContext
  order: OrderState
}) => {
  const { t, i18n } = useTranslation(['summary', 'steps', 'packages'])
  const config = useRemoteConfig()
  const settings = useDealSettingsById(deal?.dealId)
  const items = useMemo(() => Array.from(elements.values()), [elements])

  const showMonthly = shouldShowMonthly(settings)

  const group = useMemo(() => {
    if (!config) {
      return []
    }
    return items.map(({ variable, value, price, extra }) => {
      const { title, desc } = variableTls({
        variable,
        value,
        config,
        extra,
        i18n,
      })
      const priceValue = Discounts.unwrap(price)
      return {
        title,
        desc,
        icon: getVariableIcon(variable),
        price: showMonthly
          ? getConvertedPrice(priceValue, settings)
          : priceValue,
      }
    })
  }, [items, order, config, showMonthly, settings, i18n])

  const totalValue = Discounts.unwrap(price)

  return (
    <SummaryGroup
      borderBottom={borderBottom}
      priceSuffix={settings.priceSuffix}
      title={t(`summary:label.package.${item.type}`)}
      // desc={t('status:configuration.completed')}
      total={showMonthly ? getConvertedPrice(totalValue, settings) : totalValue}
      items={group}
      expanded={expanded}
    />
  )
}

export const ItemFrame = ({
  item,
  price,
  elements,
  frameCount,
  expanded,
  borderBottom,
  totalCount,
  name,
  order,
  deal,
}: ItemSummary<OrderSummaryItem> & {
  name?: string
  borderBottom?: boolean
  frameCount: number
  deal?: SessionDealContext
  expanded?: boolean
  totalCount: number
  order: OrderState
}) => {
  const { t, i18n } = useTranslation(['summary', 'packages', 'common'])
  const items = useMemo(() => Array.from(elements.values()), [elements])
  const settings = useDealSettingsById(deal?.dealId)
  const { priceSuffix } = settings
  const showMonthly = shouldShowMonthly(settings)

  const frameImage = item.frame?.url

  const config = useRemoteConfig()
  const group = useMemo(() => {
    if (!config) {
      return []
    }
    const list = items
      .map(({ variable, value, price, extra }) => {
        const { title, desc } = variableTls({
          variable,
          value,
          config,
          item,
          extra,
          i18n,
        })
        if (variable === 'intensity' && value === 'full') {
          return null
        }
        const priceValue = Discounts.unwrap(price)
        return {
          title,
          desc,
          icon: getVariableIcon(variable),
          price: showMonthly
            ? getConvertedPrice(priceValue, settings)
            : priceValue,
        }
      })
      .filter((v) => !!v)

    if (item?.temple?.id) {
      const templeId = item.temple.name || item.temple.id.toString()
      const templeQty = item.temple.store_qty

      const desc = showFrameQty(item.frame, config.region)
        ? templeId +
          space +
          t('common:qtyInStore', {
            count: templeQty,
          })
        : templeId

      list.splice(1, 0, {
        // adds to the list at index 1 (just after the frame)
        title: t('common:temple'),
        desc,
        icon: getVariableIcon('temple'),
        price: 0, // temple price not included in API
      })
    }

    return list
  }, [items, i18n, config, item, t, showMonthly, settings])

  const totalValue = Discounts.unwrap(price)
  return (
    <SummaryGroup
      borderBottom={borderBottom}
      title={t(
        `summary:label.glasses.${item.bundleType}.${
          totalCount === 1 ? 'single' : 'multiple'
        }`,
        {
          number: frameCount,
        }
      )}
      desc={name}
      total={showMonthly ? getConvertedPrice(totalValue, settings) : totalValue}
      items={group}
      expanded={expanded}
      priceSuffix={priceSuffix}
      image={frameImage}
    />
  )
}
